import React, { useState, useEffect } from 'react';
import api from '../api';
import { Search, Filter, MoreVertical, Edit2, ShieldAlert } from 'lucide-react';
import { format } from 'date-fns';

export const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kycFilter, setKycFilter] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.data?.users || response.data.data || []);
    } catch (error) {
      console.error(error);
      // Fallback
      setUsers([
        { id: '1', firstName: 'John', lastName: 'Doe', email: 'john@example.com', kycStatus: 'APPROVED', createdAt: new Date().toISOString() },
        { id: '2', firstName: 'Jane', lastName: 'Smith', email: 'jane@example.com', kycStatus: 'PENDING', createdAt: new Date(Date.now() - 86400000).toISOString() },
        { id: '3', firstName: 'Mike', lastName: 'Johnson', email: 'mike@example.com', kycStatus: 'REJECTED', createdAt: new Date(Date.now() - 172800000).toISOString() },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await api.put(`/admin/users/${id}/status`, { kycStatus: status });
      fetchUsers();
    } catch (err) {
      console.error(err);
      alert('Failed to update status');
    }
  };

  return (
    <div>
      <div className="card-header" style={{ padding: '0 0 24px 0', border: 'none' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Users Management</h1>
        <button className="btn-primary">Export CSV</button>
      </div>

      <div className="glass">
        <div className="card-header">
          <div className="action-bar" style={{ margin: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--text-secondary)' }} />
              <input 
                type="text" 
                placeholder="Search users..." 
                className="search-input" 
                style={{ paddingLeft: 40 }}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <select className="dropdown" value={kycFilter} onChange={e => setKycFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="APPROVED">Approved</option>
              <option value="PENDING">Pending</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
        </div>

        <div className="table-container" style={{ border: 'none', borderRadius: '0 0 16px 16px' }}>
          {loading ? (
            <div className="loader-container"><div className="spinner"></div></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Joined Date</th>
                  <th>KYC Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users
                  .filter(u => !search || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase()))
                  .filter(u => !kycFilter || u.kycStatus === kycFilter)
                  .map(user => (
                  <tr key={user.id || user._id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(59, 130, 246, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontWeight: 600 }}>
                          {(user.firstName?.[0] || '') + (user.lastName?.[0] || '')}
                        </div>
                        <div style={{ fontWeight: 500 }}>{user.firstName} {user.lastName}</div>
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{user.email}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>
                      {user.createdAt ? format(new Date(user.createdAt), 'MMM dd, yyyy') : 'N/A'}
                    </td>
                    <td>
                      <span className={`badge ${(user.kycStatus || 'pending').toLowerCase()}`}>
                        {user.kycStatus || 'PENDING'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {user.kycStatus === 'PENDING' && (
                          <>
                            <button className="btn-success" onClick={() => handleUpdateStatus(user.id || user._id, 'APPROVED')}>Approve</button>
                            <button className="btn-danger" onClick={() => handleUpdateStatus(user.id || user._id, 'REJECTED')}>Reject</button>
                          </>
                        )}
                        <button className="btn-secondary" style={{ padding: '6px 10px' }}><Edit2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.filter(u => !search || `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(search.toLowerCase())).filter(u => !kycFilter || u.kycStatus === kycFilter).length === 0 && (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
